<template>
  <div 
    class="messages-container d-flex flex-column overflow-auto pr-3"
    ref="messagesContainer"
  >
    <div
      v-for="msg in shownMessages"
      :key="msg.id"
      class="message-row"
      :class="{ owned: isOwnedMessage(msg) }"
    >
      <div v-if="!isOwnedMessage(msg)" class="avatar">
        {{ getInitials(msg.sender.name) }}
      </div>
      
      <!-- Message content -->
      <div
        v-if="msg.type === 'text' || msg.type === 'close-request'"
        class="message p-3 pb-5"
        :class="{ ownedMessage: isOwnedMessage(msg) }"
      >
        <span v-if="msg.type === 'text'" class="message-text">{{ msg.content }}</span>
        <span v-else class="font-italic">{{ closeRequestText(msg) }}</span>

        <span class="timestamp">{{ formatTime(msg.created_at) }}</span>
      </div>

      <!-- File message -->
      <div
        v-else-if="msg.type === 'file'"
        class="message file-message p-3 pb-5"
        :class="{ ownedMessage: isOwnedMessage(msg) }"
      >
        <div v-if="fileData[msg.id]" class="file-content">
          <i class="far fa-file file-icon"></i>
          <button
            v-if="!fileDownloading[msg.id]"
            class="btn btn-link p-0 ml-auto"
            @click="downloadFile(msg)"
          >
            <span class="file-name">{{ fileData[msg.id].name }}</span>
          </button>
          <em v-else class="ml-auto">Downloading...</em>
        </div>
        <div v-else>
          <em v-if="fileLoading[msg.id]">Loading file info...</em>
          <button
            v-else
            class="btn btn-link p-0"
            @click="fetchFileHash(msg)"
          >
            Load file info
          </button>
        </div>
        <span class="timestamp">{{ formatTime(msg.created_at) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import store from '../../store.js';
import { closeRequestText, shownInConversation } from '../../utils/chatMessages.js';

export default {
  name: 'ChatMessages',
  props: {
    messages: {
      type: Array,
      required: true
    },
    isWorkfixLine: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      fileData: {},
      fileLoading: {},
      fileUrls: {},
      fileDownloading: {}
    };
  },
  watch: {
    messages: {
      handler(msgs) {
        msgs.forEach(msg => {
          if (msg.type === 'file' && !this.fileData[msg.id] && !this.fileLoading[msg.id]) {
            this.fetchFileHash(msg);
          }
        });
        this.$nextTick(this.scrollToBottom)
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.$nextTick(this.scrollToBottom)
  },
  computed: {
    shownMessages() {
      return shownInConversation(this.messages);
    },
    getUser(){
      return this.$store.getters.getUserData;
    }
  },
  methods: {
    closeRequestText(msg) {
      return closeRequestText(msg.content, this.isWorkfixLine, this.isOwnedMessage(msg) ? 'You' : msg.sender.name);
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer.parentElement;
      if (!container) return;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    },
    isOwnedMessage(msg) {
      return msg.sender.email === this.getUser.email;
    },
    formatTime(isoString) {
      const date = new Date(isoString);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    getInitials(name) {
      const parts = name.split(' ');
      if (parts.length > 1) {
        return (
          parts[0].charAt(0).toUpperCase() +
          parts[1].charAt(0).toUpperCase()
        );
      }
      return name.charAt(0).toUpperCase();
    },
    async fetchFileHash(msg) {
      this.$set(this.fileLoading, msg.id, true);
      try {
        const res = await store.dispatch('documents/getDocument', {
          ApiClient: Vue.prototype.$ApiClient,
          departments: this.$store.getters.getUserData.departments, 
          id: msg.content
        });
        if (res.status === 200 && res.data && res.data.data && res.data.data.file) {
          const file = res.data.data.file;
          this.$set(this.fileData, msg.id, {
            hash: file.hash,
            name: file.filename || file.name
          });
        }
      } catch (err) {
        console.error('Error fetching file hash', err);
      } finally {
        this.$set(this.fileLoading, msg.id, false);
      }
    },
    async downloadFile(msg) {
      const data = this.fileData[msg.id];
      if (!data) return;
      
      this.$set(this.fileDownloading, msg.id, true);
      try {
        const res = await store.dispatch('documents/downloadDocument', {
          ApiClient: Vue.prototype.$ApiClient,
          departments: this.$store.getters.getUserData.departments, 
          hash: data.hash
        });
        if (res.status === 200) {
          const url = URL.createObjectURL(res.data);
          const link = document.createElement('a');
          link.href = url;
          link.download = data.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          this.$set(this.fileUrls, msg.id, url);
        }
      } catch (err) {

      } finally {
        this.$set(this.fileDownloading, msg.id, false);
      }
    }
  }
};
</script>

<style scoped>
  .messages-container {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    overflow: auto;
  }
  .message-row {
    display: flex;
    align-items: flex-start;  
  }
  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 5px;
    background-color: #146F70;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 1.6rem;
    margin-right: 0.5rem;
  }
  .message {
    position: relative;
    background-color: rgba(20,111,112,0.10);
    font-size: 1.45rem;
    border-radius: 10px;
    align-self: flex-start;
    max-width: 50%;
    min-width: 10%;
  }
  .message-text {
    white-space: pre-wrap;
    word-break: break-word;
  }
  .ownedMessage {
    background-color: rgba(244,245,247,1.00);
    align-self: flex-end;
  }
  .file-message .file-content {
    display: flex;
    align-items: center;
  }
  .file-icon {
    font-size: 2.5rem;
    margin-right: 0.75rem;
    color: #666;
  }
  .file-name {
    flex-grow: 1;
    font-weight: 600;
    font-size: 1.45rem;
    color: #6c757d;
  }
  .timestamp {
    position: absolute;
    bottom: 0.5rem;
    right: 0.75rem;
    font-size: 1rem;
    color: #666;
  }
  .owned .message {
    margin-left: auto;
  }
</style>
